import { useCallback, useContext, useMemo } from 'react';
import _ from 'underscore';
import { saveAssetGroupAndLinkedData } from '@bingads-webui-campaign-react/perf-max-step-wizard';
import Promise from 'bluebird';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { ScenarioContext, ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { logCopilotRecommendations } from '@bingads-webui-campaign-react/copilot-common';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import {
  getLoggerWithoutPageInfo,
  createVideoRepository,
  createImageRepository,
  createFolderRepository,
  uploadVideoMetadata,
} from '@bingads-webui-campaign-react/asset-library-services';
import { getLogVersion } from '@bingads-webui-campaign-react/asset-library-model';
import { newVideoFromMTObject } from '@bingads-webui-campaign-react/image-experience-video-part';
import { getODataConfig } from '../../shared/components/odata-config';
import { createDefaultCampaign, handleDefaultCampaignCreationError } from '../../shared/model/create-default-campaign';
import { getDefaultAssetGroup } from '../../shared/model/default-data';

const GENERIC_ERROR_MESSAGE_KEY = _TL_("We've encountered an error and can't continue. Please contact support for further assistance.");

export const useAssetStepRepository = () => {
  const scenarioContext = useContext(ScenarioContext);
  const i18n = useContext(I18nContext);
  const permissions = useContext(PermissionsContext);
  const { scope } = useContext(ScopeContext) || {};
  const [recommendationContext] = useContext(RecommendationContext);
  const activity = useContext(ActivityContext);

  const { state, send } = useWizardService();

  const versionForLog = getLogVersion(permissions);

  const logger = getLoggerWithoutPageInfo(activity, versionForLog, 'PMaxAdsStep');

  const serverContext = useContext(ServerContext);

  const {
    PMaxNewAspectRatios: isPMaxNewAspectRatiosEnabled,
    PMaxExcludeNewAspectRatios: isPMaxNewAspectRatiosExcluded,
  } = serverContext.DynamicPermissions || {};

  const isPMaxNewAspectRatios = isPMaxNewAspectRatiosEnabled && !isPMaxNewAspectRatiosExcluded;

  const { aid, cid } = scope.values;

  const oDataConfig = useMemo(() => getODataConfig(serverContext), [serverContext]);

  const videoRepository = createVideoRepository({
    oDataConfig,
    aid,
    cid,
  });

  const folderRepository = createFolderRepository({
    i18n,
    logger,
    oDataConfig,
    aid,
    cid,
  });

  const imageRepository = createImageRepository({
    oDataConfig,
    aid,
    cid,
    logger,
    i18n,
    activity,
  });

  const uploadVideoIfNeeded = useCallback(({ assetGroup }) => {
    const videoPromises = [];

    if (!assetGroup.videos) {
      return Promise.resolve(assetGroup);
    }

    // check for videos in the assetGroup that dont have an id.
    assetGroup.videos.forEach((video) => {
      if (!video.id) {
        videoPromises.push(uploadVideoMetadata({
          videoMetadata: video,
          newVideoFromMTObject,
          folderRepository,
          imageRepository,
          videoRepository,
        }).then((newVideo) => {
          _.extend(video, newVideo);
        }));
      }
    });

    if (_.isEmpty(videoPromises)) {
      return Promise.resolve(assetGroup);
    }

    return Promise.all(videoPromises).then(() => assetGroup).catch(((err) => {
      activity.trace('[PmaxNorthStarWizard] - Error uploading video metadata', { ...err });

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        serverErrors: [i18n.getString(GENERIC_ERROR_MESSAGE_KEY)],
      });
    }));
  }, [folderRepository, imageRepository, videoRepository, activity, i18n]);

  const load = useCallback(() => {
    const { assetGroup: savedAssetGroup, campaign: savedCampaign } = state.context.wizardData;

    if (isPMaxNewAspectRatios) {
      activity.trace('[PmaxNorthStarWizard] - New Aspect Ratios is enabled');
    }

    const saveCampaignsPromise = savedCampaign
      ? Promise.resolve(savedCampaign)
      : createDefaultCampaign({
        i18n,
        serverContext,
        scenarioContext,
      }).then((result) => {
        const { campaign } = result;

        activity.trace('[PmaxNorthStarWizard] - Default Campaign saved');
        activity.signal(`[PmaxNorthStarWizard] - Default Campaign saved, campaignId: ${campaign.data.Id}`);

        send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { campaign: result.campaign.data });

        return result.campaign.data;
      }).catch((errorData) => {
        activity.trace('[PmaxNorthStarWizard] - Default Campaign creation failed', { ...errorData });

        handleDefaultCampaignCreationError({ i18n, scope, errorData });

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({ ...errorData });
      });

    const savedAssetGroupPromise = savedAssetGroup ? Promise.resolve(savedAssetGroup) : Promise.resolve(getDefaultAssetGroup({ i18n }));

    return Promise.props({ campaign: saveCampaignsPromise, assetGroup: savedAssetGroupPromise }).then(({ campaign, assetGroup }) => ({
      campaign,
      assetGroup,
    })).catch((errorData) => {
      activity.trace(`[PmaxNorthStarWizard] loadData failed. Error: ${errorData}`);

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        error: {
          ...errorData,
        },
      });
    });
  }, [activity, i18n, isPMaxNewAspectRatios, scenarioContext, scope, send, serverContext, state.context.wizardData]);

  const save = useCallback(({
    data,
    initialData,
  }) => {
    const savedCampaign = state.context.wizardData.campaign;

    const { assetGroup: assetGroupToSave } = data;

    const { assetGroup: initialAssetGroup } = initialData;

    if (isPMaxNewAspectRatios) {
      activity.trace('[PmaxNorthStarWizard] - New Aspect Ratios is enabled');
    }

    const saveCampaignsPromise = savedCampaign
      ? Promise.resolve({
        campaign: {
          data: savedCampaign,
        },
      }) :
      createDefaultCampaign({
        i18n,
        serverContext,
        scenarioContext,
      });

    return saveCampaignsPromise.then((result) => {
      const { campaign } = result;

      activity.trace('[PmaxNorthStarWizard] - Default Campaign saved');
      activity.signal(`[PmaxNorthStarWizard] - Default Campaign saved, campaignId: ${campaign.data.Id}`);

      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { campaign: result.campaign.data });

      return {
        campaign: result.campaign.data,
      };
    }).then((result) => {
      const assetGroupChanges = {
        assetGroup: {
          data: assetGroupToSave,
        },
        originalAssetGroup: {
          data: initialAssetGroup,
        },
        campaign: result.campaign,
      };

      const saveContext = {
        scenario: scenarioContext.name,
        activity,
        activityLogger: scenarioContext.createActivity('saveAssets'),
        i18n,
        serverContext,
        cid,
        aid,
      };

      return Promise.props({
        campaign: Promise.resolve(result.campaign),
        savedAssetGroup: uploadVideoIfNeeded({ assetGroup: assetGroupToSave })
          .then(() => saveAssetGroupAndLinkedData(assetGroupChanges, saveContext)),
      });
    }).then((result) => {
      const { savedAssetGroup } = result;

      activity.trace(`[PmaxWizard] saveData succeeded. Assets count: ${result}`);

      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { assetGroup: savedAssetGroup.assetGroup.data });

      logCopilotRecommendations({
        recommendationContext,
        assetGroup: savedAssetGroup.assetGroup.data,
        campaignId: result.campaign.Id,
      });

      return {
        campaign: result.campaign,
        assetGroup: savedAssetGroup.assetGroup.data,
      };
    }).catch((errorData) => {
      activity.trace(`[PmaxWizard] saveData failed. Error: ${errorData}`);

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        error: {
          ...errorData,
        },
      });
    });
  }, [activity, aid, cid, i18n, recommendationContext, uploadVideoIfNeeded, scenarioContext, send, serverContext, state.context.wizardData.campaign]);

  return {
    load,
    save,
  };
};
